import { createSpeechServicesPonyfill, fetchAuthorizationToken } from 'web-speech-cognitive-services';
import SpeechRecognition from 'react-speech-recognition';

const region = (import.meta.env.VITE_TTS_REGION ?? '').trim();
const subscriptionKey = (import.meta.env.VITE_TTS_KEY ?? '').trim();
const speechTokenUrl = (import.meta.env.VITE_SPEECH_TOKEN_URL ?? '').trim();
const apiBase = `${import.meta.env.VITE_FLETCH_API_URL}/fletch/`;

function resolveSpeechTokenRequestUrl(): string | undefined {
  if (!speechTokenUrl) {
    return undefined;
  }
  if (speechTokenUrl.startsWith('http://') || speechTokenUrl.startsWith('https://')) {
    return speechTokenUrl;
  }
  const path = speechTokenUrl.startsWith('/') ? speechTokenUrl.slice(1) : speechTokenUrl;
  return `${apiBase}${path}`;
}

async function fetchBackendAuthorizationToken(): Promise<string> {
  const url = resolveSpeechTokenRequestUrl();
  if (!url) {
    throw new Error('Speech token URL is not configured');
  }
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'fletch-api-key': `${import.meta.env.VITE_FLETCH_API_KEY}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Speech token request failed: ${res.status}`);
  }
  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    const data = (await res.json()) as { token?: string; authorizationToken?: string };
    const token = data.token ?? data.authorizationToken;
    if (!token || typeof token !== 'string') {
      throw new Error('Speech token response missing token');
    }
    return token.trim();
  }
  return (await res.text()).trim();
}

export function setupSpeechPolyfill(): boolean {
  if (!region) {
    return false;
  }

  const tokenUrlResolved = resolveSpeechTokenRequestUrl();

  type TokenCredentials = () => Promise<{ region: string; authorizationToken: string }>;

  let credentials: TokenCredentials;

  if (tokenUrlResolved) {
    credentials = async () => ({
      region,
      authorizationToken: await fetchBackendAuthorizationToken(),
    });
  } else if (subscriptionKey) {
    // Never pass `subscriptionKey` into the ponyfill — it logs a console warning. Exchange for a short-lived token instead.
    credentials = async () => ({
      region,
      authorizationToken: await fetchAuthorizationToken({ region, subscriptionKey }),
    });
  } else {
    return false;
  }

  try {
    const { SpeechRecognition: AzureSpeechRecognition } = createSpeechServicesPonyfill({
      credentials,
    });
    SpeechRecognition.applyPolyfill(AzureSpeechRecognition);
    return true;
  } catch {
    return false;
  }
}
